<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import { useOnixKnowledgeStore } from 'dashboard/store/onix/knowledge';
import OnixPageLayout from 'dashboard/components-next/onix/OnixPageLayout.vue';
import OnixEmptyState from 'dashboard/components-next/onix/OnixEmptyState.vue';
import KnowledgeTopicCard from 'dashboard/components-next/onix/KnowledgeTopicCard.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const router = useRouter();
const { accountScopedRoute } = useAccount();
const knowledgeStore = useOnixKnowledgeStore();

const dialogRef = ref(null);
const topicName = ref('');

const collections = computed(() => knowledgeStore.getCollections);
const uiFlags = computed(() => knowledgeStore.getUIFlags);

const countLabel = count => {
  const total = Number(count) || 0;
  if (total === 1) return t('ONIX.KNOWLEDGE.ONE_TEXT');
  return t('ONIX.KNOWLEDGE.MANY_TEXTS', { count: total });
};

const openCreate = () => {
  topicName.value = '';
  dialogRef.value?.open();
};

const createTopic = async () => {
  const name = topicName.value.trim();
  if (!name) return;
  const data = await knowledgeStore.create({ collection: { name } });
  if (!data?.id) {
    useAlert(t('ONIX.KNOWLEDGE.ERROR_SAVE'));
    return;
  }
  dialogRef.value?.close();
  router.push(
    accountScopedRoute('onix_knowledge_show', { collectionId: data.id })
  );
};

const openTopic = id => {
  router.push(accountScopedRoute('onix_knowledge_show', { collectionId: id }));
};

onMounted(() => {
  knowledgeStore.fetch();
});
</script>

<template>
  <OnixPageLayout
    :header-title="t('ONIX.KNOWLEDGE.TITLE')"
    :description="t('ONIX.KNOWLEDGE.DESCRIPTION')"
    :button-label="t('ONIX.KNOWLEDGE.ADD')"
    :button-policy="['administrator']"
    :is-fetching="uiFlags.fetchingList"
    :is-empty="!collections.length"
    @click="openCreate"
  >
    <template #emptyState>
      <OnixEmptyState
        :title="t('ONIX.KNOWLEDGE.EMPTY_TITLE')"
        :subtitle="t('ONIX.KNOWLEDGE.EMPTY_SUBTITLE')"
        :action-perms="['administrator']"
      >
        <template #preview>
          <div class="flex flex-col gap-4 p-px">
            <KnowledgeTopicCard
              v-for="sample in [
                t('ONIX.KNOWLEDGE.SAMPLE_ONE'),
                t('ONIX.KNOWLEDGE.SAMPLE_TWO'),
                t('ONIX.KNOWLEDGE.SAMPLE_THREE'),
              ]"
              :key="sample"
              :name="sample"
              :count-label="t('ONIX.KNOWLEDGE.MANY_TEXTS', { count: 3 })"
            />
          </div>
        </template>
        <template #actions>
          <Button
            :label="t('ONIX.KNOWLEDGE.ADD')"
            icon="i-lucide-plus"
            size="sm"
            @click="openCreate"
          />
        </template>
      </OnixEmptyState>
    </template>
    <template #body>
      <div class="flex flex-col gap-4">
        <KnowledgeTopicCard
          v-for="collection in collections"
          :key="collection.id"
          :name="collection.name"
          :count-label="countLabel(collection.chunks_count)"
          @open="openTopic(collection.id)"
        />
      </div>
    </template>
  </OnixPageLayout>

  <Dialog
    ref="dialogRef"
    :title="t('ONIX.KNOWLEDGE.CREATE_TOPIC')"
    :confirm-button-label="t('ONIX.KNOWLEDGE.CREATE_TOPIC')"
    :disable-confirm-button="!topicName.trim()"
    :is-loading="uiFlags.creatingItem"
    @confirm="createTopic"
  >
    <Input
      v-model="topicName"
      :label="t('ONIX.KNOWLEDGE.TOPIC_NAME')"
      :placeholder="t('ONIX.KNOWLEDGE.TOPIC_PLACEHOLDER')"
      @enter="createTopic"
    />
  </Dialog>
</template>
